/**
 * The processor's OutcomeMessage stays verbatim English (it is the truth, written for screen readers). In
 * Spanish, one plain sentence generated from the OutcomeCode sits under it as a second line — never in its
 * place. The table is `outcomeGloss.<code>` in the language files, one sentence for each of the 19 codes.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export function useOutcomeGloss(): (code: string | null | undefined) => string | null {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  return useCallback(
    (code: string | null | undefined) => {
      if (!code || lang !== 'es') return null;
      const key = `outcomeGloss.${code}`;
      return i18n.exists(key) ? t(key) : null;
    },
    [i18n, lang, t],
  );
}

export function OutcomeGloss({ code, className = 'gloss' }: { code: string | null | undefined; className?: string }) {
  const gloss = useOutcomeGloss()(code);
  if (!gloss) return null;
  return (
    <span className={className} lang="es" data-testid="outcome-gloss">
      {gloss}
    </span>
  );
}
